import React, { useState } from 'react';
import { SearchIcon, ChevronRightIcon, CheckSquareIcon, ClockIcon, AlertCircleIcon, FilterIcon, SortAscIcon, CheckIcon, XIcon, ChevronDownIcon, UserIcon, TagIcon, CalendarIcon } from 'lucide-react';
interface Task {
  id: string;
  title: string;
  description: string;
  category: string;
  service: string;
  priority: 'high' | 'medium' | 'low';
  status: 'pending' | 'in_progress' | 'completed' | 'rejected';
  deadline: string;
  assignedTo: string;
  assignedBy: string;
  createdAt: string;
  applicationId: string;
}
const tasks: Task[] = [{
  id: 'T1',
  title: 'Review Business License Application',
  description: 'Complete initial review of new business license application for downtown retail store',
  category: 'Business & Operational Licensing',
  service: 'New Business License',
  priority: 'high',
  status: 'pending',
  deadline: '2024-02-20',
  assignedTo: 'Meseret Bekele',
  assignedBy: 'System',
  createdAt: '2024-02-15',
  applicationId: 'BL-2024-0123'
}, {
  id: 'T2',
  title: 'Approve Property Tax Exemption',
  description: 'Review and process property tax exemption request for religious institution',
  category: 'Municipal Taxes & Revenue',
  service: 'Tax Exemption',
  priority: 'high',
  status: 'in_progress',
  deadline: '2024-02-21',
  assignedTo: 'Meseret Bekele',
  assignedBy: 'Kebede Alemu',
  createdAt: '2024-02-14',
  applicationId: 'PTX-2024-0456'
}, {
  id: 'T3',
  title: 'Inspect Construction Site',
  description: 'Conduct final inspection for new residential construction project',
  category: 'Land & Property Services',
  service: 'Building Permit',
  priority: 'medium',
  status: 'pending',
  deadline: '2024-02-22',
  assignedTo: 'Meseret Bekele',
  assignedBy: 'System',
  createdAt: '2024-02-16',
  applicationId: 'BP-2024-0789'
}, {
  id: 'T4',
  title: 'Process Marriage Certificate',
  description: 'Review and process marriage certificate application',
  category: 'Family & Civil Status',
  service: 'Marriage Registration',
  priority: 'medium',
  status: 'completed',
  deadline: '2024-02-19',
  assignedTo: 'Meseret Bekele',
  assignedBy: 'System',
  createdAt: '2024-02-17',
  applicationId: 'MC-2024-0321'
}, {
  id: 'T5',
  title: 'Review Environmental Permit',
  description: 'Assess environmental impact study for new development project',
  category: 'Environmental Services',
  service: 'Environmental Permit',
  priority: 'low',
  status: 'rejected',
  deadline: '2024-02-23',
  assignedTo: 'Meseret Bekele',
  assignedBy: 'Tigist Solomon',
  createdAt: '2024-02-13',
  applicationId: 'EP-2024-0654'
}];
const priorityColors = {
  high: 'text-red-600 bg-red-50',
  medium: 'text-orange-600 bg-orange-50',
  low: 'text-blue-600 bg-blue-50'
};
const statusColors = {
  pending: 'text-yellow-600 bg-yellow-50',
  in_progress: 'text-blue-600 bg-blue-50',
  completed: 'text-green-600 bg-green-50',
  rejected: 'text-red-600 bg-red-50'
};
const TaskStatusBadge = ({
  status
}: {
  status: Task['status'];
}) => {
  const statusLabels = {
    pending: 'Pending',
    in_progress: 'In Progress',
    completed: 'Completed',
    rejected: 'Rejected'
  };
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[status]}`} data-id="element-5281">
      {statusLabels[status]}
    </span>;
};
const TaskPriorityBadge = ({
  priority
}: {
  priority: Task['priority'];
}) => {
  const priorityLabels = {
    high: 'High',
    medium: 'Medium',
    low: 'Low'
  };
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${priorityColors[priority]}`} data-id="element-5282">
      {priorityLabels[priority]}
    </span>;
};
const TaskCard = ({
  task
}: {
  task: Task;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  return <div className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:border-blue-500 transition-colors duration-200" data-id="element-5283">
      <div className="p-4" data-id="element-5284">
        <div className="flex items-start justify-between" data-id="element-5285">
          <div className="flex-1" data-id="element-5286">
            <div className="flex items-center space-x-2 mb-2" data-id="element-5287">
              <TaskStatusBadge status={task.status} data-id="element-5288" />
              <TaskPriorityBadge priority={task.priority} data-id="element-5289" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-5290">
              {task.title}
            </h3>
            <p className="text-sm text-gray-500 mb-2" data-id="element-5291">
              Application ID: {task.applicationId}
            </p>
            <div className="flex items-center space-x-4 text-sm text-gray-500" data-id="element-5292">
              <div className="flex items-center" data-id="element-5293">
                <CalendarIcon className="h-4 w-4 mr-1" data-id="element-5294" />
                <span data-id="element-5295">Due {new Date(task.deadline).toLocaleDateString()}</span>
              </div>
              <div className="flex items-center" data-id="element-5296">
                <TagIcon className="h-4 w-4 mr-1" data-id="element-5297" />
                <span data-id="element-5298">{task.category}</span>
              </div>
            </div>
          </div>
          <button onClick={() => setIsExpanded(!isExpanded)} className="ml-4 text-gray-400 hover:text-gray-600" data-id="element-5299">
            <ChevronDownIcon className={`h-5 w-5 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-5300" />
          </button>
        </div>
        {isExpanded && <div className="mt-4 pt-4 border-t border-gray-100" data-id="element-5301">
            <p className="text-sm text-gray-600 mb-4" data-id="element-5302">{task.description}</p>
            <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-5303">
              <div data-id="element-5304">
                <p className="text-gray-500" data-id="element-5305">Assigned To</p>
                <p className="font-medium text-gray-900" data-id="element-5306">{task.assignedTo}</p>
              </div>
              <div data-id="element-5307">
                <p className="text-gray-500" data-id="element-5308">Assigned By</p>
                <p className="font-medium text-gray-900" data-id="element-5309">{task.assignedBy}</p>
              </div>
              <div data-id="element-5310">
                <p className="text-gray-500" data-id="element-5311">Created On</p>
                <p className="font-medium text-gray-900" data-id="element-5312">
                  {new Date(task.createdAt).toLocaleDateString()}
                </p>
              </div>
              <div data-id="element-5313">
                <p className="text-gray-500" data-id="element-5314">Service Type</p>
                <p className="font-medium text-gray-900" data-id="element-5315">{task.service}</p>
              </div>
            </div>
            <div className="mt-4 flex space-x-3" data-id="element-5316">
              {task.status === 'pending' && <>
                  <button className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700" data-id="element-5317">
                    Start Processing
                  </button>
                  <button className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-50" data-id="element-5318">
                    Reassign
                  </button>
                </>}
              {task.status === 'in_progress' && <>
                  <button className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-700" data-id="element-5319">
                    Complete
                  </button>
                  <button className="flex-1 bg-red-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-700" data-id="element-5320">
                    Reject
                  </button>
                </>}
            </div>
          </div>}
      </div>
    </div>;
};
export const TasksPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedPriority, setSelectedPriority] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.title.toLowerCase().includes(searchQuery.toLowerCase()) || task.description.toLowerCase().includes(searchQuery.toLowerCase()) || task.applicationId.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = selectedStatus === 'all' || task.status === selectedStatus;
    const matchesPriority = selectedPriority === 'all' || task.priority === selectedPriority;
    const matchesCategory = selectedCategory === 'all' || task.category === selectedCategory;
    return matchesSearch && matchesStatus && matchesPriority && matchesCategory;
  });
  const categories = Array.from(new Set(tasks.map(task => task.category)));
  return <div className="min-h-screen bg-gray-50" data-id="element-5321">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-5322">
        {/* Breadcrumb */}
        <div className="flex items-center text-sm text-gray-500 mb-8" data-id="element-5323">
          <a href="/" className="hover:text-gray-700" data-id="element-5324">
            Dashboard
          </a>
          <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-5325" />
          <span className="text-gray-900" data-id="element-5326">My Tasks</span>
        </div>
        {/* Header */}
        <div className="mb-8" data-id="element-5327">
          <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5328">My Tasks</h1>
          <p className="text-gray-600" data-id="element-5329">
            Manage and track your assigned tasks across all municipal services
          </p>
        </div>
        {/* Task Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8" data-id="element-5330">
          <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-5331">
            <div className="text-sm text-gray-500" data-id="element-5332">Total Tasks</div>
            <div className="text-2xl font-semibold text-gray-900" data-id="element-5333">
              {tasks.length}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-5334">
            <div className="text-sm text-gray-500" data-id="element-5335">Pending Tasks</div>
            <div className="text-2xl font-semibold text-yellow-600" data-id="element-5336">
              {tasks.filter(t => t.status === 'pending').length}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-5337">
            <div className="text-sm text-gray-500" data-id="element-5338">Due Today</div>
            <div className="text-2xl font-semibold text-red-600" data-id="element-5339">
              {tasks.filter(t => new Date(t.deadline).toDateString() === new Date().toDateString()).length}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-5340">
            <div className="text-sm text-gray-500" data-id="element-5341">Completed Tasks</div>
            <div className="text-2xl font-semibold text-green-600" data-id="element-5342">
              {tasks.filter(t => t.status === 'completed').length}
            </div>
          </div>
        </div>
        {/* Filters */}
        <div className="bg-white p-4 rounded-lg border border-gray-200 mb-8" data-id="element-5343">
          <div className="flex flex-col md:flex-row md:items-center md:space-x-4" data-id="element-5344">
            <div className="flex-1" data-id="element-5345">
              <div className="relative" data-id="element-5346">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-5347">
                  <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-5348" />
                </div>
                <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Search tasks by title, description, or application ID..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-5349" />
              </div>
            </div>
            <div className="mt-4 md:mt-0 flex flex-wrap gap-4" data-id="element-5350">
              <select className="block w-full md:w-auto px-3 py-2 border border-gray-300 rounded-md text-sm" value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)} data-id="element-5351">
                <option value="all" data-id="element-5352">All Statuses</option>
                <option value="pending" data-id="element-5353">Pending</option>
                <option value="in_progress" data-id="element-5354">In Progress</option>
                <option value="completed" data-id="element-5355">Completed</option>
                <option value="rejected" data-id="element-5356">Rejected</option>
              </select>
              <select className="block w-full md:w-auto px-3 py-2 border border-gray-300 rounded-md text-sm" value={selectedPriority} onChange={e => setSelectedPriority(e.target.value)} data-id="element-5357">
                <option value="all" data-id="element-5358">All Priorities</option>
                <option value="high" data-id="element-5359">High Priority</option>
                <option value="medium" data-id="element-5360">Medium Priority</option>
                <option value="low" data-id="element-5361">Low Priority</option>
              </select>
              <select className="block w-full md:w-auto px-3 py-2 border border-gray-300 rounded-md text-sm" value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} data-id="element-5362">
                <option value="all" data-id="element-5363">All Categories</option>
                {categories.map(category => <option key={category} value={category} data-id="element-5364">
                    {category}
                  </option>)}
              </select>
            </div>
          </div>
        </div>
        {/* Task List */}
        <div className="space-y-4" data-id="element-5365">
          {filteredTasks.length > 0 ? filteredTasks.map(task => <TaskCard key={task.id} task={task} data-id="element-5366" />) : <div className="text-center py-12 bg-white rounded-lg border border-gray-200" data-id="element-5367">
              <CheckSquareIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-5368" />
              <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-5369">
                No tasks found
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-5370">
                No tasks match your current filters. Try adjusting your search
                criteria.
              </p>
            </div>}
        </div>
      </div>
    </div>;
};